import { FloatingPane, FloatingPaneMenuItem, FloatingPaneSeparator } from '../FloatingPane';
import { usePortalContainer } from '../PortalContainerContext';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '@thinkalpha/ui-core/utils';
import * as React from 'react';

export const DROPDOWN_MENU_CONTENT_PADDING = 4;
const DROPDOWN_MENU_PADDING_CLASS = `p-[4px]`;

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

// Context-aware DropdownMenuPortal
const DropdownMenuPortal = ({
    container,
    ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Portal>) => {
    const contextContainer = usePortalContainer();
    return <DropdownMenuPrimitive.Portal container={container ?? contextContainer} {...props} />;
};

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
        inset?: boolean;
        chevronSide?: 'left' | 'right';
    }
>(({ className, inset, chevronSide, children, ...props }, ref) => (
    <DropdownMenuPrimitive.SubTrigger
        ref={ref}
        className={cn(
            'flex cursor-default items-center rounded-xs px-2 py-1.5 text-md outline-hidden select-none focus:bg-accent data-[state=open]:bg-accent',
            inset && 'pl-8',
            props.disabled && 'opacity-50',
            className,
        )}
        {...props}
    >
        {chevronSide === 'left' && (
            <div className="relative mr-2 h-4 w-4">
                <i className="fas fa-chevron-left" />
            </div>
        )}
        {children}
        {chevronSide === 'right' && (
            <div className="relative ml-auto h-4 w-4">
                <i className="fas fa-chevron-right" />
            </div>
        )}
    </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & { container?: HTMLElement }
>(({ className, asChild, children, container, ...props }, ref) => (
    <DropdownMenuPortal container={container}>
        <DropdownMenuPrimitive.SubContent ref={ref} asChild {...props}>
            <FloatingPane
                asChild={asChild}
                className={cn(
                    'max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-32 overflow-y-auto shadow-lg',
                    DROPDOWN_MENU_PADDING_CLASS,
                    className,
                )}
            >
                {children}
            </FloatingPane>
        </DropdownMenuPrimitive.SubContent>
    </DropdownMenuPortal>
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & { container?: HTMLElement }
>(({ className, sideOffset = 4, asChild, children, container, ...props }, ref) => (
    <DropdownMenuPortal container={container}>
        <DropdownMenuPrimitive.Content ref={ref} sideOffset={sideOffset} asChild {...props}>
            <FloatingPane
                className={cn(
                    'max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-32 overflow-y-auto',
                    DROPDOWN_MENU_PADDING_CLASS,
                    className,
                )}
                asChild={asChild}
            >
                {children}
            </FloatingPane>
        </DropdownMenuPrimitive.Content>
    </DropdownMenuPortal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
        inset?: boolean;
    }
>(({ className, inset, asChild, children, ...props }, ref) => (
    <DropdownMenuPrimitive.Item ref={ref} asChild {...props}>
        <FloatingPaneMenuItem className={cn(inset && 'pl-8', className)} asChild={asChild}>
            {children}
        </FloatingPaneMenuItem>
    </DropdownMenuPrimitive.Item>
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
    // `asChild` does not apply here with multiple children
    Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>, 'asChild'>
>(({ className, children, checked, ...props }, ref) => (
    <DropdownMenuPrimitive.CheckboxItem ref={ref} checked={checked} asChild {...props}>
        <FloatingPaneMenuItem className={cn('pr-2 pl-8 data-[disabled]:pointer-events-none', className)}>
            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                <DropdownMenuPrimitive.ItemIndicator>
                    <FontAwesomeIcon icon={faCheck} />
                </DropdownMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </FloatingPaneMenuItem>
    </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
    // `asChild` does not apply here with multiple children
    Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>, 'asChild'>
>(({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.RadioItem ref={ref} asChild {...props}>
        <FloatingPaneMenuItem className={cn('pr-2 pl-8 data-[disabled]:pointer-events-none', className)}>
            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                <DropdownMenuPrimitive.ItemIndicator>
                    <FontAwesomeIcon icon={faCircle} size="xs" />
                </DropdownMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </FloatingPaneMenuItem>
    </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
        inset?: boolean;
    }
>(({ className, inset, ...props }, ref) => (
    <DropdownMenuPrimitive.Label
        ref={ref}
        className={cn('px-2 py-1.5 text-sm font-semibold text-gray-500 dark:text-gray-400', inset && 'pl-8', className)}
        {...props}
    />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
    Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>, 'asChild'>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Separator ref={ref} asChild {...props}>
        <FloatingPaneSeparator className={className} />
    </DropdownMenuPrimitive.Separator>
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
    return <span className={cn('ml-auto text-sm tracking-widest opacity-60', className)} {...props} />;
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuCheckboxItem,
    DropdownMenuRadioItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuGroup,
    DropdownMenuPortal,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuRadioGroup,
};
